import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useDashboard } from '../hooks/useDashboard';
import { AppTopBar } from './AppTopBar';
import { TodayHeader } from './TodayHeader';
import { SectionHeader } from './SectionHeader';
import { ChecklistItem } from './ChecklistItem';
import { TaskCard } from './TaskCard';
import { SkillCard } from './SkillCard';
import { InternEmptyState, InternErrorState } from './shared/InternEmptyState';
import { DashboardSkeleton } from './shared/InternSkeletons';

export function DashboardPage() {
  const { user } = useAuth();
  const { userProfile } = usePermissions();
  const {
    data,
    loading,
    error,
    refresh,
    toggleChecklistItem,
    updateTaskStatus,
    updateSkillProgress,
    setOpenTimer,
  } = useDashboard();

  const userName =
    userProfile?.display_name ||
    user?.email?.split('@')[0] ||
    'there';

  // Greeting for onboarding first load
  const isFirstLoad = !loading && data && data.checklistItems.every((i) => !i.entry?.checked);

  const openTimer = data?.openTimer ?? null;
  const activeTasks = data?.projectTasks.filter((t) => t.status !== 'done') ?? [];
  const redTasks = activeTasks.filter((t) => t.escalation === 'red' || t.is_critical);
  const checklistDone = data?.checklistItems.filter((i) => i.entry?.checked).length ?? 0;
  const checklistTotal = data?.checklistItems.length ?? 0;
  const visibleSkills = data?.skillItems.filter((s) => s.status !== 'explored') ?? [];

  return (
    <div
      className="min-h-screen"
      style={{ background: 'var(--color-warm-white)' }}
    >
      <AppTopBar
        openTimer={openTimer}
        onTimerChange={setOpenTimer}
        userName={userName}
      />

      <main className="mx-auto max-w-[880px] px-6 py-10">
        {loading && <DashboardSkeleton />}

        {error && <InternErrorState onRetry={refresh} />}

        {!loading && !error && data && (
          <div className="space-y-12">
            {/* Today header */}
            <TodayHeader
              userName={userName}
              banner={data.banner}
              projectTasks={data.projectTasks}
              checklistDone={checklistDone}
              checklistTotal={checklistTotal}
            />

            {/* "Needs attention now" strip — only if hot tasks exist */}
            {redTasks.length > 0 && (
              <section aria-label="Needs attention now">
                <div className="rounded-[4px] border border-[rgba(154,41,41,0.3)] bg-[rgba(154,41,41,0.04)] px-4 py-3 mb-6">
                  <p className="text-[13px] font-[600] text-[var(--destructive)] uppercase tracking-wide mb-4">
                    Needs attention now
                  </p>
                  <div className="space-y-4">
                    {redTasks.map((task) => {
                      const latestNote = null; // loaded in task detail
                      return (
                        <TaskCard
                          key={task.id}
                          task={task}
                          latestNote={latestNote}
                          onStatusChange={updateTaskStatus}
                        />
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* Section A: Daily Checklist */}
            <section aria-label="Today's Checklist">
              <SectionHeader
                title="Today's Checklist"
                summary={
                  checklistTotal > 0
                    ? `${checklistDone} / ${checklistTotal} done`
                    : undefined
                }
              />
              {data.checklistItems.length === 0 ? (
                <InternEmptyState
                  title="No standing checklist items yet"
                  body="Ken will add your daily recurring tasks here."
                />
              ) : (
                <div>
                  {data.checklistItems.map((item) => (
                    <ChecklistItem
                      key={item.id}
                      item={item}
                      onToggle={toggleChecklistItem}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Section B: Project To-Dos */}
            <section aria-label="Your Tasks">
              <SectionHeader
                title="Your Tasks"
                summary={
                  activeTasks.length > 0
                    ? `${activeTasks.length} task${activeTasks.length !== 1 ? 's' : ''}`
                    : 'All done'
                }
              />
              {data.projectTasks.length === 0 ? (
                <InternEmptyState
                  title="No project tasks right now."
                  body="Nice — head down to Build Your Skills."
                />
              ) : (
                <div className="space-y-4">
                  {data.projectTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      latestNote={null}
                      onStatusChange={updateTaskStatus}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Section C: Skill Development */}
            <section aria-label="Build Your Skills">
              <SectionHeader
                title="Build Your Skills"
                summary={
                  visibleSkills.length > 0
                    ? 'fresh picks'
                    : undefined
                }
              />
              {visibleSkills.length === 0 ? (
                <InternEmptyState
                  title="Nothing queued yet"
                  body="Tell us what you'd like to dig into — Ken will add something fresh."
                />
              ) : (
                <div className="space-y-4">
                  {visibleSkills.map((skill) => (
                    <SkillCard
                      key={skill.id}
                      item={skill}
                      onComplete={(id) => updateSkillProgress(id, 'explored')}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Welcome note for first load */}
            {isFirstLoad && !data.banner && (
              <div
                className="rounded-[4px] px-5 py-4 text-center"
                style={{ background: 'var(--color-warm-white)', border: '1px dashed var(--color-gold)', borderRadius: '4px' }}
              >
                <p className="text-[15px] font-[500] text-[var(--color-charcoal)]">
                  Welcome — check off your daily items, work your tasks top-down, and when you're caught up, build a skill. Hit Start to track your time.
                </p>
              </div>
            )}

            {/* Bottom padding */}
            <div className="h-24" aria-hidden />
          </div>
        )}
      </main>
    </div>
  );
}
